<script setup lang="ts">
// RU/EN switch of the console language: two small segmented buttons. The
// choice is remembered in localStorage (shared/i18n setLocale).
import { LOCALES, locale, setLocale, type Locale } from '@/shared/i18n'
import { common } from './messages'

function short(l: Locale): string {
  return l === 'ru' ? common.t('localeRu') : common.t('localeEn')
}

/** Each language named in itself, never translated: whoever cannot read the current one still finds theirs. */
const ENDONYM: Record<Locale, string> = { ru: 'Русский', en: 'English' }

function title(l: Locale): string {
  return ENDONYM[l]
}
</script>

<template>
  <div class="ag-locale" role="group" :aria-label="common.t('localeSwitch')" data-test="locale-switch">
    <button
      v-for="l in LOCALES"
      :key="l"
      type="button"
      class="ag-locale__btn"
      :class="{ 'ag-locale__btn--on': locale === l }"
      :aria-pressed="locale === l"
      :title="title(l)"
      :lang="l"
      :data-locale="l"
      @click="setLocale(l)"
    >
      {{ short(l) }}
    </button>
  </div>
</template>

<style scoped>
.ag-locale {
  display: inline-flex;
  flex: none;
  border: 1px solid var(--ag-line-strong);
  border-radius: var(--ag-radius-sm, 4px);
  overflow: hidden;
}

.ag-locale__btn {
  padding: 2px var(--ag-space-2);
  border: 0;
  background: transparent;
  color: var(--ag-text-dim);
  font: inherit;
  font-size: var(--ag-font-size-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
}

.ag-locale__btn + .ag-locale__btn {
  border-left: 1px solid var(--ag-line-strong);
}

.ag-locale__btn--on {
  background: var(--ag-bg-raised);
  color: var(--ag-text);
}

.ag-locale__btn:focus-visible {
  outline: 2px solid var(--ag-air);
  outline-offset: -2px;
}
</style>
